import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { SearchCheck, CircleDot, Lightbulb, Check } from "lucide-react";

const UI = {
  tr: { btn: "Klişe Denetimi", running: "İnceleniyor...", clean: "Bu bölümde belirgin klişe veya zayıf ifade bulunmadı.", found: "işaretli ifade", reason: "Neden", suggestion: "Uzman önerisi", apply: "Öneriyi uygula", applied: "Uygulandı", tooShort: "Denetim için en az 20 karakter yazın.", verdict: "Uzman görüşü" },
  en: { btn: "Cliché Check", running: "Reviewing...", clean: "No obvious clichés or weak phrases found in this section.", found: "flagged phrases", reason: "Why", suggestion: "Expert suggestion", apply: "Apply suggestion", applied: "Applied", tooShort: "Write at least 20 characters to check.", verdict: "Expert verdict" },
};

function Highlighted({ text, phrases }) {
  if (!phrases.length) return <>{text}</>;
  const re = new RegExp(`(${phrases.map((p) => p.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "g");
  return <>{text.split(re).map((part, i) => phrases.includes(part) ? <mark key={i} className="bg-[#fde68a] text-[#1a2e4a] rounded px-0.5">{part}</mark> : <span key={i}>{part}</span>)}</>;
}

export const ClicheCheck = ({ cvId, lang, question, text, onApply }) => {
  const u = UI[lang];
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [applied, setApplied] = useState({});

  const run = async () => {
    if (text.trim().length < 20) return toast.error(u.tooShort);
    setBusy(true);
    setApplied({});
    try {
      const { data } = await api.post(`/cvs/${cvId}/statement/cliche-check`, { language: lang, question, text });
      setResult(data);
    } catch (e) { toast.error(e.response?.data?.detail || "AI error"); }
    finally { setBusy(false); }
  };

  const apply = (i, issue) => {
    if (!text.includes(issue.phrase)) return toast.error("Text changed");
    onApply(text.replace(issue.phrase, issue.suggestion));
    setApplied((a) => ({ ...a, [i]: true }));
  };

  const active = result ? result.issues.filter((_, i) => !applied[i] && text.includes(result.issues[i].phrase)) : [];

  return (
    <div className="mt-2">
      <Button data-testid={`cliche-check-btn-${question}`} size="sm" variant="outline" disabled={busy} onClick={run} className="border-[#1a2e4a] text-[#1a2e4a] h-8">
        {busy ? <CircleDot className="h-3.5 w-3.5 mr-1.5 animate-pulse" /> : <SearchCheck className="h-3.5 w-3.5 mr-1.5" />} {busy ? u.running : u.btn}
      </Button>
      {result && (
        <div data-testid={`cliche-result-${question}`} className="mt-3 space-y-3">
          {result.overall && <div className="text-[12px] text-[#334155] bg-white border border-[#e2e8f0] rounded-md px-3 py-2"><b className="text-[#1a2e4a]">{u.verdict}:</b> {result.overall}</div>}
          {active.length === 0 ? (
            <div data-testid={`cliche-clean-${question}`} className="text-[12px] text-green-700 flex items-center gap-1.5"><Check className="h-3.5 w-3.5" /> {u.clean}</div>
          ) : (
            <>
              <div data-testid={`cliche-highlight-${question}`} className="text-[12.5px] leading-relaxed whitespace-pre-wrap bg-white border border-[#e2e8f0] rounded-md px-3 py-2 text-[#334155]">
                <Highlighted text={text} phrases={active.map((i) => i.phrase)} />
              </div>
              <div className="text-[11px] font-semibold text-[#c0392b]">{active.length} {u.found}</div>
              <ul className="space-y-2">
                {result.issues.map((issue, i) => applied[i] || !text.includes(issue.phrase) ? null : (
                  <li key={i} data-testid={`cliche-issue-${question}-${i}`} className="text-[12.5px] bg-white border-l-3 border border-[#e2e8f0] border-l-[#c0392b] rounded-md px-3 py-2">
                    <div className="font-semibold text-[#1a2e4a]">“{issue.phrase}”</div>
                    <div className="text-[#64748b] mt-0.5"><b>{u.reason}:</b> {issue.reason}</div>
                    <div className="mt-1 flex items-start gap-1.5 text-[#334155]"><Lightbulb className="h-3.5 w-3.5 shrink-0 mt-0.5 text-amber-500" /><span><b>{u.suggestion}:</b> {issue.suggestion}</span></div>
                    {issue.suggestion && <button data-testid={`cliche-apply-${question}-${i}`} onClick={() => apply(i, issue)} className="mt-1.5 text-[11px] font-semibold text-[#c0392b] hover:underline">{u.apply} →</button>}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
};
